/**
 * Choosing — and correcting — the post-processing tier.
 *
 * `PostFX.setQuality` has always existed and was never called, so every device
 * ran the `high` chain: a three-iteration bloom at half resolution, chromatic
 * aberration, vignette, grain and a blurred grade. That is the right chain for
 * the desktop GPU it was tuned on and the wrong one for a phone, which pushes
 * comparable pixel counts through a fraction of the fill rate.
 *
 * ## Why a guess is not enough
 *
 * There is no honest way to ask a browser how fast its GPU is. `hardwareConcurrency`
 * counts CPU cores, `deviceMemory` is bucketed and absent on iOS, and the
 * unmasked renderer string is behind an extension that privacy modes disable.
 * Any static rule that is cautious enough to protect a budget Android also
 * strips the bloom off a flagship iPhone that could run the full chain — and
 * that is the effect the game is built around.
 *
 * So the tier is chosen in two stages. `recommendedQuality()` makes a cheap
 * opening guess from the one signal that is reliable and relevant — is this a
 * touch device, i.e. is it running on a mobile GPU — and `QualityGovernor`
 * then watches real frame times and steps down if the guess was optimistic.
 *
 * ## The governor moves the glow, never the picture
 *
 * The post tier is the governor's only lever. Render resolution used to be a
 * rung below `low` (0.67x, then 0.5x), and that is what made the cards "blurry
 * at the start of every game, then sharp": a bad stretch in one game spent the
 * rungs, the next game opened on the degraded resolution, and the climb back
 * took six seconds a rung with the player watching. Resolution is now fixed by
 * `budgetedResolution` for the session. And the tier itself cannot soften a
 * card either — the card layer is composited around bloom and aberration (see
 * `CardShield`), so `high`, `medium` and `low` draw the same card pixels.
 *
 * The governor is also reset for every game (`reset()`): a tier lost in one
 * game is not carried into the next, which is judged afresh from its own frames.
 */

export type Quality = 'high' | 'medium' | 'low';

/**
 * Ceiling on rendered pixels.
 *
 * History, because this number has been wrong in both directions.
 *
 * It was 3.4MP so a DPR-3 phone could render at native resolution, on the
 * reasoning that 390x844 at 3x is 2.96MP — fewer pixels than a 1920x1080
 * desktop already renders at 2x. c828426 cut it to 1.4MP because that compared
 * pixel counts between machines whose fill rates are nowhere near comparable,
 * and 3x multiplied every fragment in the frame by 2.25.
 *
 * 1.4MP then had a cost of its own that nobody was measuring: at DPR 3 it made
 * the canvas 804x1741 for a 1170x2532 screen, and the browser stretched it
 * 1.45x. Every card was soft by exactly that stretch — before the governor ever
 * touched anything — and the atlas was baked for the smaller number too.
 *
 * 4MP lets a phone-shaped viewport (up to ~444k CSS px, which is every phone,
 * 390x844 and 430x932 included) render at its native 3x. It is the same budget
 * c828426 thought unaffordable, with the cause of the unaffordability moved:
 * the post chain is what the governor spends (it is the only thing it spends),
 * and `CardShield` keeps it off the cards. Measured on an RTX 2080 Super at
 * 390x844 / DPR 3, medium tier: frame gap unchanged (median 16.6ms, vsync-bound,
 * at 1x and 4x CPU throttle), GPU time per frame 2.9ms -> 5.4ms for 2.1x the
 * pixels plus the card capture. A phone's fill rate is not that card's — which
 * is what the governor, now reset per game, is for. Larger viewports (tablets,
 * desktops, a DPR-3 laptop) still fall back toward `MAX_RESOLUTION`, so nothing
 * but phones moves.
 */
const RESOLUTION_BUDGET = 4_000_000;

/**
 * The resolution every device gets regardless of the budget. Beyond 2x nobody
 * can see the difference on a large display, and below it desktops would
 * visibly soften.
 */
export const MAX_RESOLUTION = 2;

/**
 * The resolution a viewport of this size can afford.
 *
 * Lives here, next to the governor that spends it, and is used by two callers
 * that have to agree: the renderer, which draws at it, and the card atlas,
 * which is baked for it. Baking finer than the framebuffer can show is memory
 * and load time spent on detail that is resolved away before anyone sees it.
 *
 * @param floor - the resolution this is never pushed below whatever the budget
 *   says. Desktops sit here.
 */
export function budgetedResolution(width: number, height: number, floor: number): number {
  const dpr = globalThis.devicePixelRatio || 1;
  const affordable = Math.sqrt(RESOLUTION_BUDGET / Math.max(1, width * height));
  return Math.min(dpr, Math.max(floor, affordable));
}

const LADDER: readonly Quality[] = ['high', 'medium', 'low'] as const;

/** The ladder's first rung with this tier, or the top one if it is not on it. */
function rungOf(quality: Quality): number {
  return Math.max(0, LADDER.indexOf(quality));
}

/**
 * The opening guess.
 *
 * `(pointer: coarse)` is the test rather than a width or a user-agent string:
 * width says nothing (a landscape phone is 844 CSS px, a small laptop window is
 * narrower than that), and user-agent sniffing is both unreliable and actively
 * being frozen. Coarse pointer means a finger, and a finger means a mobile GPU
 * essentially without exception.
 *
 * Touch devices open at `medium`, which differs from `high` only in bloom
 * iterations (2 rather than 3) and its downsample (4 rather than 2). Both are
 * blur quality on an effect that is already a wide soft glow, so the visible
 * cost is very small and the fill-rate saving is roughly fourfold.
 *
 * `medium` is safe to open on because it cannot soften a card: the card layer
 * is composited around bloom and aberration, so the first frame of a game is
 * as sharp as the thousandth whichever tier the governor is on. (It used to be
 * the other way round — `medium` laid bloom over the cards and `low` did not,
 * so the governor's first demotion, a few seconds in, was what sharpened them.)
 */
export function recommendedQuality(): Quality {
  const mm = globalThis.matchMedia?.bind(globalThis);
  if (typeof mm !== 'function') return 'high';
  try {
    return mm('(pointer: coarse)').matches ? 'medium' : 'high';
  } catch {
    return 'high';
  }
}

/**
 * Mean frame time that counts as "this device cannot afford the frame".
 *
 * 28ms is about 35fps. It was 22ms — 45fps — and that was too tight by far in a
 * game whose action is *deliberately* bursty: a pile resolving is supposed to
 * cost a second of particles, bloom and shake. A threshold that treats the most
 * expensive intended moment in the game as a fault will fire on a machine that
 * is otherwise perfectly happy, and then the player pays for it.
 */
const DEMOTE_MS = 28;

/**
 * Mean frame time comfortable enough to earn a rung back.
 *
 * The gap between this and `DEMOTE_MS` is the dead band, and it has to be wide
 * or the governor oscillates — which is a glow that visibly pulses in and out.
 *
 * It is deliberately not expressed as a fraction of the refresh rate. On a
 * 120Hz MacBook a healthy frame is 8ms and on a 60Hz panel it is 16.7, and a
 * threshold of 20ms is comfortably above both — while a rate-relative rule
 * would demand 120fps from a laptop that was doing nothing wrong at 60.
 */
const PROMOTE_MS = 20;

/**
 * Frames longer than this are thrown away rather than counted.
 *
 * A tab switch, a garbage collection, an alt-tab or a paused debugger all
 * produce a single enormous `dt`. Averaged in, one of them outweighs a hundred
 * good frames and would demote a machine that is running perfectly well.
 */
const OUTLIER_MS = 250;

/**
 * Ignored startup period, per game.
 *
 * The first seconds of a game are the opening deal, the first flips and a pile
 * of first-use uploads all at once. It is the slowest a game ever runs and says
 * nothing about the steady state. `reset()` starts it again, so every game gets
 * it, not just the first.
 */
const WARMUP_SECONDS = 1.5;

/** How much continuous evidence each decision needs. */
const WINDOW_SECONDS = 2;

/**
 * Good windows in a row before a rung is handed back.
 *
 * Asymmetric on purpose: quick to protect the framerate, slow to spend it
 * again. Three windows is six seconds of genuinely comfortable frames.
 */
const RECOVER_WINDOWS = 3;

/**
 * Windows after a promotion during which a demotion is taken as proof that the
 * level just tried is simply not affordable on this machine.
 *
 * This is the ratchet that stops oscillation. Climb, fail quickly, and that
 * rung is written off until the next game; climb and hold, and it is kept.
 */
const PROBATION_WINDOWS = 2;

export interface QualityGovernorOptions {
  /** Where to start. Defaults to `recommendedQuality()`. */
  initial?: Quality;
  /** Called whenever the tier changes, including the initial one. */
  onChange: (quality: Quality) => void;
}

/**
 * The tier the game is actually rendering at right now.
 *
 * The governor owns it, but the rank ceremony is drawn by a renderer of its
 * own — off to the side of the game's — and needs the same answer without
 * being handed a reference to the governor through four layers of UI. It is a
 * read of live state, not a second source of truth: only `#apply` writes it.
 */
let live: Quality = 'high';

export function liveQuality(): Quality {
  return live;
}

/**
 * Watches frame times and spends — or recovers — the post chain to keep them good.
 *
 * Feed it `dt` in **seconds** from the render loop, and call `reset()` when a
 * game begins.
 *
 * ## Why this climbs back, when it used to only fall
 *
 * The original only ever stepped down, so every transient dip was permanent:
 * a background tab waking, a garbage collection or one unusually loud pile
 * would cost the glow for the rest of the session with no way back. So it
 * recovers, and the oscillation that design feared is handled properly instead
 * of avoided: a wide dead band between the two thresholds, three good windows
 * before any climb, and a ratchet that writes a rung off if taking it fails
 * immediately.
 */
export class QualityGovernor {
  /** The level a game starts at: what was asked for, or what the player pinned. */
  #start: number;
  #index: number;
  /**
   * The best level still believed affordable this game. Never goes back down
   * within a game: a rung that has failed on probation has proved something
   * about this machine for as long as the game lasts. `reset()` forgives it.
   */
  #ceiling = 0;
  #sincePromotion = Number.POSITIVE_INFINITY;
  #goodWindows = 0;

  readonly #onChange: (quality: Quality) => void;

  #warmup = 0;
  #accumulated = 0;
  #frames = 0;

  constructor(options: QualityGovernorOptions) {
    this.#start = rungOf(options.initial ?? recommendedQuality());
    this.#index = this.#start;
    this.#ceiling = this.#start;
    this.#onChange = options.onChange;
    this.#onChange(this.quality);
  }

  get quality(): Quality {
    return LADDER[this.#index] ?? 'high';
  }

  /** True once there is nothing left to give up. */
  get exhausted(): boolean {
    return this.#index >= LADDER.length - 1;
  }

  #pinned: Quality | null = null;

  /** The tier the player pinned in Settings, or null while the governor decides. */
  get pinned(): Quality | null {
    return this.#pinned;
  }

  /**
   * Hold one tier, or let go.
   *
   * A pin is the player's word over the governor's: sampling stops, the tier
   * is applied, and it stays until the pin is lifted — at which point the
   * governor resumes from the recommended tier as if freshly built, so a
   * device that was pinned low for a week gets its picture back.
   */
  pin(quality: Quality | null): void {
    this.#pinned = quality;
    this.#start = rungOf(quality ?? recommendedQuality());
    this.#restart();
  }

  /**
   * Begin a game: back to the start level, with nothing remembered.
   *
   * Called from `resetBoard`, which every way of starting a round goes through.
   * Without it the governor is a session-long state machine and a game inherits
   * whatever the last one left it in — a tier spent in a bad stretch, a
   * half-full window, a rung written off — and the opening frames of the next
   * game, the ones a player looks at hardest, are drawn at the leftovers.
   *
   * A pinned tier stays pinned: the start level *is* the pin.
   */
  reset(): void {
    this.#restart();
  }

  #restart(): void {
    this.#ceiling = this.#start;
    this.#sincePromotion = Number.POSITIVE_INFINITY;
    this.#goodWindows = 0;
    this.#warmup = 0;
    this.#accumulated = 0;
    this.#frames = 0;
    this.#apply(this.#start);
  }

  /** `dt` in seconds, as the ticker reports it. */
  sample(dt: number): void {
    if (this.#pinned !== null) return;
    if (!Number.isFinite(dt) || dt <= 0) return;

    const ms = dt * 1000;
    if (ms > OUTLIER_MS) return;

    if (this.#warmup < WARMUP_SECONDS) {
      this.#warmup += dt;
      return;
    }

    this.#accumulated += ms;
    this.#frames += 1;
    if (this.#accumulated < WINDOW_SECONDS * 1000) return;

    const mean = this.#accumulated / this.#frames;
    this.#accumulated = 0;
    this.#frames = 0;
    this.#sincePromotion += 1;

    if (mean > DEMOTE_MS) {
      this.#goodWindows = 0;
      this.#demote();
      return;
    }

    if (mean < PROMOTE_MS) {
      this.#goodWindows += 1;
      if (this.#goodWindows >= RECOVER_WINDOWS) this.#promote();
      return;
    }

    // Between the two: comfortable enough to keep, not comfortable enough to
    // spend. Sitting here forever is the correct outcome.
    this.#goodWindows = 0;
  }

  #demote(): void {
    // Failing this soon after climbing to the current level is proof the level
    // above is not affordable here. Write it off rather than trying it again
    // and again, which is what would make the glow pulse.
    // `#ceiling` is the best index still allowed, and the level that just failed
    // is the one being left — so the ceiling becomes the level *below* it. Set
    // to `#index` instead, it would permit an immediate climb straight back
    // into the rung that has just proved unaffordable, which is the pacing this
    // is here to prevent.
    if (this.#sincePromotion <= PROBATION_WINDOWS) {
      this.#ceiling = Math.max(this.#ceiling, this.#index + 1);
    }

    if (this.#index >= LADDER.length - 1) return;
    this.#apply(this.#index + 1);
  }

  #promote(): void {
    if (this.#index <= this.#ceiling) return;
    this.#goodWindows = 0;
    this.#sincePromotion = 0;
    this.#apply(this.#index - 1);
  }

  /** Move to a level and tell the host only about what actually changed. */
  #apply(next: number): void {
    const before = this.quality;
    this.#index = next;
    live = this.quality;
    if (this.quality !== before) this.#onChange(this.quality);
  }
}
